import { ReactNode, SyntheticEvent, useState } from 'react';
import { format } from 'date-fns-tz';
import { Typography, Box } from '@mui/material';
import { IconButton } from '@components/iconButton';
import { Menu, IMenuItem } from '@components/menu';
import { ExclamationCircleColored, CheckCircleColored, ExclamationTriangleColored, Times } from '@assets/svgs';
import { LongDateFormatWithoutSeconds, getDateFormatter } from '@utils/date';
import { CardContainer } from './SidebarCard.styles';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

export type CardType = 'UnderwritingIssue' | 'SubjectivityItem' | 'UnderwritingActivity';

const defaultIconStyle = { height: '18px', width: '18px', alignSelf: 'flex-end' };

type IconStatus = 'active' | 'closed' | 'accepted' | 'open' | 'rejected';
type CardTypeKey = 'SubjectivityItem' | 'UnderwritingIssue' | 'UnderwritingActivity';

type IconStyle = React.CSSProperties;

type IconsByCardTypeFn = (iconStyle?: IconStyle) => Record<CardTypeKey, { [key in IconStatus]?: ReactNode }>;

export const iconsByCardType: IconsByCardTypeFn = (iconStyle = defaultIconStyle) => ({
  SubjectivityItem: {
    active: <ExclamationTriangleColored title='Active' style={iconStyle} />,
    closed: <CheckCircleColored title='Closed' style={iconStyle} />,
  },
  UnderwritingIssue: {
    accepted: <CheckCircleColored title='Accepted' style={iconStyle} />,
    open: <ExclamationTriangleColored title='Open' style={iconStyle} />,
    rejected: <ExclamationCircleColored title='Rejected' style={iconStyle} />,
  },
  UnderwritingActivity: {
    closed: <CheckCircleColored title='Accepted' style={iconStyle} />,
    accepted: <CheckCircleColored title='Accepted' style={iconStyle} />,
    open: <ExclamationTriangleColored title='Open' style={iconStyle} />,
    rejected: <ExclamationCircleColored title='Rejected' style={iconStyle} />,
  },
});

interface ISidebarCard {
  children: ReactNode;
  cardType: CardType;
  title: string;
  statusText: string;
  date?: { createdAt: string; resolvedAt?: string };
  status?: string;
  onClick?: () => void;
  actionItems?: IMenuItem[];
  hideDelete?: boolean;
  isDisabled?: boolean;
}

export const SidebarCard = ({
  children,
  statusText,
  status,
  cardType,
  title,
  date,
  onClick,
  actionItems,
  hideDelete,
  isDisabled,
}: ISidebarCard) => {
  const icons = iconsByCardType(defaultIconStyle)[cardType];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (cardType === 'SubjectivityItem') {
      handleOnClickMenuItem(0);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleOnClickMenuItem = (index: number, _?: string, event?: SyntheticEvent) => {
    event?.stopPropagation();
    if (!actionItems) return;
    if (actionItems[index]?.action !== undefined) {
      actionItems[index].action?.();
      setAnchorEl(null);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const hasActionItems = !!actionItems?.length;

  return (
    <CardContainer
      onClick={onClick}
      hasActionItems={hasActionItems}
      sx={{ cursor: isDisabled ? 'default' : 'pointer', position: 'relative' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            svg: {
              fill: (theme) =>
                theme.palette.theme === 'lio' ? theme.palette.success.dark : `${theme.palette.success.main} !important`,
            },
          }}
        >
          {status && icons[status as keyof typeof icons]}

          <Typography
            variant='caption'
            sx={{
              lineHeight: '12px',
              paddingLeft: '5px',
            }}
          >
            {statusText}
          </Typography>
        </Box>
        {cardType === 'UnderwritingIssue' && date ? (
          <Typography
            variant='caption'
            sx={{
              lineHeight: '14px',
              display: 'inherit',
            }}
          >
            {date.resolvedAt
              ? format(new Date(date.resolvedAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))
              : format(new Date(date?.createdAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))}
          </Typography>
        ) : null}
        {hasActionItems && (
          <>
            {!hideDelete && (
              <IconButton
                title='deleteSubjectivity'
                onClick={handleMenuClick}
                sx={{ height: '36px', width: '36px', alignItems: 'center' }}
              >
                <Times />
              </IconButton>
            )}
            <Menu
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={handleMenuClose}
              onClickMenuItem={handleOnClickMenuItem}
              items={actionItems}
            />
          </>
        )}
      </Box>
      <Box
        sx={{
          padding: '10px 5px 10px 20px',
          display: 'flex',
          flexDirection: 'column',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        {cardType !== 'UnderwritingIssue' && date ? (
          <Typography
            variant='caption'
            sx={{
              lineHeight: '14px',
              display: 'inherit',
            }}
          >
            {`${new Date(date.createdAt).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })} ${
              date.resolvedAt
                ? ` - ${new Date(date.resolvedAt).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}`
                : ''
            }`}
          </Typography>
        ) : null}
        <Typography
          variant='bodySmall'
          sx={{
            color: 'text.main',
            paddingTop: '2px',
            display: 'inherit',
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    </CardContainer>
  );
};
